import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Typography } from 'antd';
import LocationPicker from 'components/LocationPicker';
import { getZoneFromAddress } from 'constants/operations';
import { useSelector } from 'react-redux';
import { authProfile } from 'redux/auth/selectors';
import { useDebounce } from 'util/Hooks';
import { useMutation } from '@tanstack/react-query';

const useResolveZone = () => {
  const user = useSelector(authProfile);
  return useMutation({
    mutationFn: async ({ address }: { address: string }) => {
      const resolvedZone = await getZoneFromAddress(
        address,
        Number(user?.logisticOperatorID),
      );
      if (!resolvedZone.lat || !resolvedZone.lng) {
        throw new Error(
          'No se pudieron resolver las coordenadas para la dirección proporcionada.',
        );
      }
      return resolvedZone;
    },
  });
};

const GeolocationField = () => {
  const {
    values,
    initialValues,
    errors,
    setValues,
    setFieldError,
    validateField,
    getFieldMeta,
    setStatus,
  } = useFormikContext<{
    address: string;
    lat: number | null;
    lng: number | null;
  }>();
  const { address } = values;
  const isTouched = getFieldMeta('address').touched;
  const debouncedAddress = useDebounce(address, 500);
  const { mutate: resolveZone } = useResolveZone();
  const lastResolved = useRef<{
    address: unknown;
    lat: number | null;
    lng: number | null;
  } | null>(null);
  const failedAddress = useRef<unknown>(null);
  const wasGeolocating = useRef(false);
  const isGeolocating =
    !!isTouched &&
    !!address &&
    address !== lastResolved.current?.address &&
    address !== failedAddress.current;
  const addressError = errors.address;

  useEffect(() => {
    setStatus({ geolocating: isGeolocating });
    if (wasGeolocating.current && !isGeolocating) validateField('address');
    wasGeolocating.current = isGeolocating;
  }, [isGeolocating, setStatus, validateField]);

  useEffect(() => {
    if (isGeolocating && addressError) setFieldError('address', undefined);
  }, [isGeolocating, addressError, setFieldError]);

  useEffect(() => {
    let owner: { lat: number | null; lng: number | null } | null = null;
    if (address === lastResolved.current?.address) owner = lastResolved.current;
    else if (address === initialValues.address) owner = initialValues;
    const hasCoordinates = Boolean(Number(owner?.lat) && Number(owner?.lng));
    const lat = hasCoordinates ? owner?.lat ?? null : null;
    const lng = hasCoordinates ? owner?.lng ?? null : null;
    if (!Object.is(values.lat, lat) || !Object.is(values.lng, lng)) {
      setValues(
        (prev) => ({ ...prev, lat, lng }),
        lat !== null && lng !== null,
      );
    }
  }, [address, initialValues, values.lat, values.lng, setValues]);

  useEffect(() => {
    if (
      debouncedAddress &&
      isTouched &&
      debouncedAddress === address &&
      debouncedAddress !== lastResolved.current?.address
    ) {
      resolveZone(
        { address: debouncedAddress }, // Primer parámetro: datos para la mutación
        {
          // Segundo parámetro: opciones de la mutación
          onSuccess: (data) => {
            const resolvedAddress = data.address || debouncedAddress;
            setValues((prev) => {
              if (prev.address !== debouncedAddress) return prev;
              failedAddress.current = null;
              lastResolved.current = {
                address: resolvedAddress,
                lat: data.lat,
                lng: data.lng,
              };
              return {
                ...prev,
                address: resolvedAddress,
                resolvedZipCode: data.postalCode,
                resolvedStreet: data.street,
                resolvedNumber: data.streetNumber,
                resolvedLocality: data.locality,
                lat: data.lat,
                lng: data.lng,
              };
            });
          },
          onError: () => {
            failedAddress.current = debouncedAddress;
          },
        },
      );
    }
  }, [debouncedAddress, address, resolveZone, setValues, isTouched]);

  // Función para manejar cambios desde el mapa (NO actualiza la dirección de despacho)
  const handleLocationChange = (locationData: { lat: number; lng: number }) => {
    if (locationData.lat && locationData.lng) {
      lastResolved.current = {
        address,
        lat: locationData.lat,
        lng: locationData.lng,
      };
      setValues((prev) => ({
        ...prev,
        lat: locationData.lat,
        lng: locationData.lng,
      }));
    }
  };

  if (isGeolocating) {
    return (
      <Typography.Text type="secondary">
        Geolocalizando la dirección…
      </Typography.Text>
    );
  }

  if (address && address === failedAddress.current) {
    return addressError ? null : (
      <Typography.Text type="danger">
        No se pudo geolocalizar la dirección
      </Typography.Text>
    );
  }

  return (
    <div>
      <LocationPicker
        onLocationChange={handleLocationChange}
        lat={values?.lat}
        lng={values?.lng}
      />
    </div>
  );
};

export default GeolocationField;
